import React, { useEffect, useState, useRef } from "react";
import { Container, Nav, NavItem, StyledButton, StyledWrap } from "./styles";
import { client, useAddItemToCart, useCart, useUpsells } from "src/context/siteContext";
import { encode, decode } from "shopify-gid";
import { isSSR } from "src/utils/ssr";
import _ from "lodash";
import { SanityImage } from "src/interfaces/sanity";
import { ProductUpsells } from "src/interfaces/sanity";
import { eventData } from 'src/utils/dataLayer';
import tryGlobal from "src/lambda/tryGlobal";
import trackKlavyioAddedToCart from "src/utils/mapKlaviyoPayload";

interface Props {
  mainImage: SanityImage;
  slug?: {
    current: string;
  };
  nav: [
    {
      _key: string;
      title: string;
      sectionId: string;
    }
  ];
  id: string,
  activePack: any;
  quantity: any;
  defaultPrice: any;
  productId: any;
  notificationBar: boolean;
  mobilePhoto?: SanityImage;
  atcButton?: {
    caption?: string;
    price?: boolean;
  };
  title?: string;
  upsells: Array<ProductUpsells>;
}

const SecondaryNav = ({
  mainImage,
  slug,
  id,
  nav,
  activePack,
  quantity,
  defaultPrice,
  productId,
  notificationBar,
  mobilePhoto,
  atcButton,
  title,
  upsells,
}: Props) => {
  const { checkout } = useCart();
  const { addItemToCart } = useAddItemToCart();
  const { openUpsells } = useUpsells();
  const [adding, setAdding] = useState(false as boolean);
  const [price, setPrice] = useState(defaultPrice);
  const [available, setAvailable] = useState(false);
  const [variants, setVariants] = useState([]);
  const [activeVariantId, setActiveVariantId] = useState("" as string);
  const [compareAtPrice, setCompareAtPrice] = useState(
    undefined as string | undefined
  );
  const [check, setCheck] = useState(true);
  const [show, setShow] = useState(false);

  const navRef = useRef(null);

  const getDimensions = (el) => {
    const { height } = el.getBoundingClientRect();
    const offsetTop = el.offsetTop;
    const offsetBottom = offsetTop + height;

    return {
      height,
      offsetTop,
      offsetBottom,
    };
  };

  useEffect(() => {
    if (check) {
      const shopifyId = encode("Product", productId, {
        accessToken: process.env.GATSBY_SHOPIFY_STOREFRONT_TOKEN,
      });

      client.product.fetch(shopifyId).then((product: any) => {
        const decodedVariants = [] as any;
        product?.variants.forEach((variant: any) => {
          decodedVariants.push({
            ...variant,
            cleanId: parseInt((variant.id).id, 0),
          });
        });

        setVariants(decodedVariants);
        setActiveVariantId(decodedVariants[0]?.id as string);
        setAvailable(decodedVariants[0]?.available);

        if (decodedVariants[0]?.compareAtPrice)
          setCompareAtPrice(decodedVariants[0]?.compareAtPrice);

        setCheck(false);
      });
    }
  }, [check]);

  useEffect(() => {
    if (isSSR) return;

    const heroSection = document.getElementById("product-hero");

    if (!heroSection) return;

    const throttledCount = _.throttle(() => {
      if (!navRef.current) return;

      const { offsetBottom: heroBottom } = getDimensions(heroSection);
      const { height: headerHeight } = getDimensions(navRef.current);
      const scrollPos = window.scrollY + headerHeight;

      if (scrollPos >= heroBottom) {
        setShow(true);
      } else {
        setShow(false);
      }
    }, 300);

    window.addEventListener("scroll", throttledCount);

    return () => window.removeEventListener("scroll", throttledCount);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!available) {
      return;
    }

    const item = {
      name: title,
      id: variants[0]?.sku,
      image: variants[0]?.image?.src,
      product_id: id,
      price: parseFloat(price),
      brand: 'GroovePillows',
      variant: variants[0]?.title,
      quantity: quantity,
    };

    setAdding(true);

    dataLayer?.push({
      ...eventData('add_to_cart'),
      ecommerce: {
        currencyCode: (process.env.GATSBY_CURRENCY_SYMBOL == "$") ? "USD" : "GBP",
        add: {
          products: [item]
        },
      }
    });

    const attributes = [];
    const upsellEnabled = upsells?.enabled && upsells?.productList.length > 0;

    await addItemToCart(activeVariantId, quantity, attributes, upsellEnabled);

    // TriplePixel('AddToCart', { item: variants[0]?.sku, q: quantity });
    tryGlobal('fbq', 'track', 'AddToCart', {
      content_name: title,
      content_ids: [item.id],
      content_type: 'product_group',
      value: parseFloat(item.price),
      currency: (process.env.GATSBY_CURRENCY_SYMBOL == "$") ? "USD" : "GBP",
    });

    trackKlavyioAddedToCart(checkout, item);

    setAdding(false);

    if (!upsellEnabled) return;

    openUpsells({
      boughtItem: {
        id: productId,
        title,
        quantity,
        price,
        imageRef: mainImage,
      },
      productList: upsells?.productList,
    });
  };

  const getButtonCaption = () => {
    if (!atcButton?.caption) {
      if (activePack)
        return `Buy for ${process.env.GATSBY_CURRENCY_SYMBOL +
          activePack.price}`;

      return `Buy for ${process.env.GATSBY_CURRENCY_SYMBOL +
        parseFloat(price * quantity).toFixed(2)}`;
    }

    if (atcButton?.price) {
      if (activePack)
        return `${atcButton?.caption} ${process.env.GATSBY_CURRENCY_SYMBOL +
          activePack.price}`;

      return `${atcButton?.caption} ${process.env.GATSBY_CURRENCY_SYMBOL +
        parseFloat(price * quantity).toFixed(2)}`;
    }

    return `${atcButton?.caption}`;
  };

  return (
    <Container ref={navRef} show={show} notificationBar={notificationBar}>
      <StyledWrap>
        <Nav>
          {nav?.map((item) => (
            <NavItem key={item?._key} href={`#${item?.sectionId}`}>
              {item?.title}
            </NavItem>
          ))}
        </Nav>
        <StyledButton small={true} onClick={handleSubmit}>
          <span>{adding ? "Adding to cart" : getButtonCaption()}</span>
        </StyledButton>
      </StyledWrap>
    </Container>
  );
};

export default SecondaryNav;
